import React, { useState, useEffect } from 'react';
import { profileService } from '../../../services/profileService';

const Notification = () => {
  const [settings, setSettings] = useState({
    orderUpdates: true,
    giftReminders: true,
    memoirUpdates: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      setLoading(true);
      const response = await profileService.getNotificationSettings();
      if (response.success) {
        setSettings(response.settings || {
          orderUpdates: true,
          giftReminders: true,
          memoirUpdates: true,
        });
      } else {
        setMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to load notification settings' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (setting) => {
    setSettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await profileService.updateNotificationSettings(settings);
      if (response.success) {
        setMessage({ 
          type: 'success', 
          text: 'Notification settings updated successfully!' 
        });
      } else {
        setMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update notification settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A574]"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-0">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Notification & Reminder</h1>
      <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
        Choose what you'd like to hear from us about.
      </p>

      {message.text && (
        <div
          className={`mb-4 sm:mb-6 p-3 sm:p-4 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-6 sm:space-y-8">
        {/* Order Updates */}
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-shrink-0 mt-1">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.orderUpdates}
                onChange={() => handleToggle('orderUpdates')}
                className="sr-only peer"
                disabled={saving}
              />
              <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-[#6B635D] rounded-[4px] peer-checked:bg-transparent peer-checked:border-[#6B635D] flex items-center justify-center transition-colors">
                {settings.orderUpdates && (
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#2C2C2C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
              </div>
            </label>
          </div>
          <div className="flex-1">
            <h3 className="font-normal text-secondary text-sm sm:text-base mb-1">Order & Delivery Updates</h3>
            <p className="text-[#6B635D] font-normal text-xs sm:text-sm">
              Get notified when your orders are confirmed, shipped, or delivered.
            </p>
          </div>
        </div>

        {/* Gift Reminders */}
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-shrink-0 mt-1">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.giftReminders}
                onChange={() => handleToggle('giftReminders')}
                className="sr-only peer"
                disabled={saving}
              />
              <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-[#6B635D] rounded-[4px] peer-checked:bg-transparent peer-checked:border-[#6B635D] flex items-center justify-center transition-colors">
                {settings.giftReminders && (
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#2C2C2C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
              </div>
            </label>
          </div>
          <div className="flex-1">
            <h3 className="font-normal text-secondary text-sm sm:text-base mb-1">Future Gift Reminders</h3>
            <p className="text-[#6B635D] font-normal text-xs sm:text-sm">
              We'll remind you when it's almost time to prepare a scheduled gift.
            </p>
          </div>
        </div>

        {/* Memoir Updates */}
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-shrink-0 mt-1">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.memoirUpdates}
                onChange={() => handleToggle('memoirUpdates')}
                className="sr-only peer"
                disabled={saving}
              />
              <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-[#6B635D] rounded-[4px] peer-checked:bg-transparent peer-checked:border-[#6B635D] flex items-center justify-center transition-colors">
                {settings.memoirUpdates && (
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#2C2C2C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
              </div>
            </label>
          </div>
          <div className="flex-1">
            <h3 className="font-normal text-secondary text-sm sm:text-base mb-1">Memoir Updates</h3>
            <p className="text-[#6B635D] font-normal text-xs sm:text-sm">
              Get updates about saved drafts, publishing progress, or changes.
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-8 sm:pt-12">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 w-full sm:w-auto min-w-[250px] bg-[#D2A146] text-white font-medium hover:bg-[#C49564] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
        >
          {saving ? 'Saving...' : 'Save Notification Settings'}
        </button>
      </div>
    </div>
  );
};

export default Notification;